import React from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const ArrowRightIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M13 7l5 5m0 0l-5 5m5-5H6" />
  </svg>
);

const CheckIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
  </svg>
);

const features = [
  { title: 'Dual Language Program', body: 'Spanish-English Dual Language sections are available across grades 1–5, developing true bilingualism and cross-cultural understanding.' },
  { title: 'Arts Integration', body: 'Visual arts, music, and creative projects are woven into core subjects — every student experiences the arts as part of daily learning.' },
  { title: 'Technology', body: 'Students use technology as a tool for learning, creativity, and collaboration, preparing them for a digital world.' },
  { title: 'Special Education (ICT)', body: 'Integrated Co-Teaching classrooms pair two certified teachers to support all learners in an inclusive, general education setting.' },
  { title: 'Science & STEM', body: 'Hands-on science exploration, coding, and engineering challenges build critical thinking and problem-solving skills.' },
  { title: 'Physical Education & Wellness', body: 'Our PE program and partnerships with Asphalt Green keep students active, healthy, and building good habits for life.' },
];

const ElementaryPage = () => (
  <div className="min-h-screen bg-white">
    <Navbar />

    <main id="main-content" className="pt-0">
      {/* Hero */}
      <section
        className="relative pt-36 pb-20 bg-[#00213a] overflow-hidden"
      >
          <div
            className="pointer-events-none absolute inset-x-0 bottom-0"
            style={{ height: '4rem', background: 'linear-gradient(to bottom, rgba(0,33,58,0) 0%, rgba(255,255,255,1) 100%)' }}
            aria-hidden="true"
          />

        <div className="max-w-6xl mx-auto px-6 lg:px-8 text-center">
          <div className="flex items-center justify-center gap-3 mb-6">
            <div className="flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-sm rounded-lg border border-white/20">
              <span className="text-sm font-semibold text-white tracking-tight">Academics</span>
            </div>
          </div>
          <h1 className="text-5xl lg:text-7xl font-semibold text-white mb-6 tracking-tight" style={{ letterSpacing: '-0.02em' }}>
            Elementary School
          </h1>
          <p className="text-2xl text-white/90 font-normal max-w-3xl leading-relaxed mx-auto">
            Grades 1–5. Building strong foundations in literacy, math, the arts, and character.
          </p>
          <div className="mt-8 flex flex-wrap gap-4 justify-center">
            <a href="https://mystudent.nyc/" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-6 py-3 bg-[#fdbb2e] text-[#00213a] font-black rounded-lg hover:bg-[#ffd04e] transition-colors duration-200 shadow-sm">
              Apply on MySchools <ArrowRightIcon />
            </a>
            <a href="/dual_language_programs" className="inline-flex items-center gap-2 px-6 py-3 bg-white/10 text-white font-semibold rounded-lg border border-white/20 hover:bg-white/20 transition-colors duration-200">
              Dual Language Programs <ArrowRightIcon />
            </a>
          </div>
        </div>
      </section>

      {/* Grade overview */}
      <section className="max-w-4xl mx-auto px-6 lg:px-8 mt-12 mb-16">
        <div className="bg-white border border-gray-200 rounded-xl shadow-md p-10">
          <h2 className="text-3xl font-semibold text-gray-900 mb-4 tracking-tight" style={{ letterSpacing: '-0.02em' }}>
            Grades 1 – 5
          </h2>
          <p className="text-gray-700 leading-relaxed text-lg mb-4">
            Elementary school at PS 96 is where Lions grow. From the earliest readers in 1st grade to the independent learners in 5th grade, our teachers build on every child's strengths while laying a rigorous academic foundation.
          </p>
          <p className="text-gray-600 leading-relaxed">
            Our classrooms are collaborative and inclusive. We believe learning happens when students feel safe, supported, and challenged — and our teachers are dedicated to creating that environment every day.
          </p>
        </div>
      </section>

      {/* Program features */}
      <section className="bg-gray-50 py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="mb-12 text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white rounded-lg border border-gray-200 mb-4">
              <span className="text-sm font-semibold text-gray-700">What We Offer</span>
            </div>
            <h2 className="text-4xl font-semibold text-gray-900 tracking-tight" style={{ letterSpacing: '-0.02em' }}>
              Programs &amp; Features
            </h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((f) => (
              <div key={f.title} className="bg-white border border-gray-200 rounded-xl p-7 hover:shadow-md hover:border-[#fdbb2e]/40 transition-all duration-200">
                <div className="flex items-start gap-3 mb-3">
                  <span className="text-[#00213a] mt-0.5 flex-shrink-0"><CheckIcon /></span>
                  <h3 className="font-semibold text-gray-900">{f.title}</h3>
                </div>
                <p className="text-sm text-gray-600 leading-relaxed pl-8">{f.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#00213a] py-20">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-semibold text-white mb-6 tracking-tight" style={{ letterSpacing: '-0.02em' }}>
            Join the PS 96 Lions Family
          </h2>
          <p className="text-xl text-white/80 mb-8">
            Applications are accepted through the NYC MySchools portal. Schedule a tour to see our school in person.
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <a href="https://mystudent.nyc/" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-8 py-4 bg-[#fdbb2e] text-[#00213a] font-black rounded-xl hover:bg-[#ffd04e] transition-colors duration-200 shadow-lg">
              Apply Now <ArrowRightIcon />
            </a>
            <a href="/contact-us" className="inline-flex items-center gap-2 px-8 py-4 bg-white/10 text-white font-semibold rounded-xl border border-white/30 hover:bg-white/20 transition-colors duration-200">
              Schedule a Tour
            </a>
          </div>
        </div>
      </section>
    </main>

    <Footer />
  </div>
);

export default ElementaryPage;
